/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import clsx from 'clsx'
import {useAppDispatch} from 'helpers/store'
import {useForm} from 'react-hook-form'
import {toast} from 'utils/toastUtils'
import SellerAction from 'store/seller/SellerAction'
import UiAuthFormShell from 'components/Ui/UiAuthFormShell'
import UiInput from 'components/Ui/UiInput'
import UiPasswordInput from 'components/Ui/UiPasswordInput'
import UiButton, {UiButtonSize, UiButtonStyle, UiButtonType,} from 'components/Ui/UiButton'
import RecoveryError from './RecoveryError'

interface RecoveryAccessProps {
    setAccessGranted: (granted: boolean) => void
    params: {
        access_token?: string
        app_id?: string
        m_clk_id?: string
    }
    accessEmailRequested: string
}

const RecoveryAccessForm = (props: RecoveryAccessProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const m_clk_id = props.params.m_clk_id
    const app_id_param = props.params.app_id
    const access_token_param = props.params.access_token

    const email_address = m_clk_id ? atob(m_clk_id) : null
    const app_id = app_id_param ? atob(app_id_param) : ''

    const [recoveryError, setRecoveryError] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { register, handleSubmit } = useForm({
        defaultValues: {
            email: email_address ?? props.accessEmailRequested,
            app_id: app_id,
            access_token: access_token_param ?? '',
            new_password: '',
            confirm_password: '',
        },
    })

    const onSubmitPasswordReset = (data) => {
        if (data.new_password !== data.confirm_password) {
            toast.error(t('Passwords do not match'))
            return
        }

        setIsSubmitting(true)
        dispatch(
            SellerAction.resetPassword(
                data.email,
                data.access_token,
                data.new_password,
                data.app_id
            )
        )
            .then(() => {
                props.setAccessGranted(true)
            })
            .catch(() => {
                toast.error(t('Error, please contact support.'))
                setRecoveryError(true)
            })
            .finally(() => {
                setIsSubmitting(false)
            })
    }

    if (recoveryError) {
        return <RecoveryError />
    }

    return (
        <UiAuthFormShell
            title={t('Password recovery')}
            subtitle={t(
                'Please enter access token delivered via e-mail and set-up a new password.'
            )}
            footer={
                <div className={clsx('text-center')}>
                    <Link to={'/'}>
                        <span>{t('Back to main')}</span>
                    </Link>
                </div>
            }
        >
            <form
                autoComplete={'off'}
                className={clsx('w-full', 'space-y-3')}
                onSubmit={handleSubmit(onSubmitPasswordReset)}
            >
                <input type={'hidden'} {...register('email')} />
                <input type={'hidden'} {...register('app_id')} />

                {!access_token_param && (
                    <UiInput
                        label={t('Access token')}
                        placeholder={t('Enter access token')}
                        className={clsx('w-full')}
                        {...register('access_token')}
                        required
                    />
                )}
                {access_token_param && (
                    <input type={'hidden'} {...register('access_token')} />
                )}

                <UiPasswordInput
                    label={t('New password')}
                    placeholder={t('Enter new password')}
                    className={clsx('w-full')}
                    {...register('new_password')}
                    required
                />

                <UiPasswordInput
                    label={t('Confirm password')}
                    placeholder={t('Confirm password')}
                    className={clsx('w-full')}
                    {...register('confirm_password')}
                    required
                />

                <div className={clsx('flex', 'justify-center', 'pt-2')}>
                    <UiButton
                        id={'btn-password-reset-submit'}
                        style={UiButtonStyle.Primary}
                        size={UiButtonSize.Medium}
                        type={UiButtonType.Submit}
                        disabled={isSubmitting}
                        title={t('Reset password')}
                    />
                </div>
            </form>
        </UiAuthFormShell>
    )
}

export default RecoveryAccessForm
